import {
  REFINE_BOQ_STATE_CODE,
  REFINE_SETTLED_STATE_CODES,
  bumpRefinePollGeneration,
  buildRefineBoqReview,
  getBoqRowsForRefinement,
  hasStructuralRefineChanges,
  isStaleRefinePollResponse,
  normalizeRefineBoqResult,
} from 'v2/apps/clink/pages/boq/boqRefinement';
import {
  fetchRefineBoqStatus,
  refineBoqWithAI,
} from './asyncThunk';

const normalizeStatus = (status) =>
  String(status ?? '')
    .trim()
    .toUpperCase();

const omitPackageFlag = (map, packageId) => {
  const next = { ...map };
  delete next[packageId];
  return next;
};

const isStalePostResponse = (state, action) => {
  const packageId = action.meta?.arg?.packageId;
  const activeRequestId = state.refineById?.[packageId]?.requestId;
  const responseRequestId = action.meta?.requestId;
  return Boolean(
    activeRequestId &&
      responseRequestId &&
      activeRequestId !== responseRequestId
  );
};

const applyRefineResponse = (state, action) => {
  const packageId = action.meta?.arg?.packageId;
  if (packageId == null) return;

  const response = action.payload?.data || action.payload || {};
  const status = normalizeStatus(response.status);
  const current = state.refineById?.[packageId] || {};
  if (REFINE_SETTLED_STATE_CODES.includes(current.stateCode)) {
    return;
  }
  const base = {
    ...current,
    status,
    processing_type: response.processing_type ?? current.processing_type ?? null,
    current_step: response.current_step ?? current.current_step ?? 0,
    total_steps: response.total_steps ?? current.total_steps ?? 0,
    received_at: response.received_at ?? current.received_at ?? null,
    completed_at: response.completed_at ?? current.completed_at ?? null,
    result: null,
    apiErrorCode: null,
    apiErrorType: null,
    apiErrorMessage: null,
    httpStatus: null,
  };

  if (status === 'PENDING') {
    state.refineById[packageId] = {
      ...base,
      stateCode: REFINE_BOQ_STATE_CODE.PENDING,
    };
    return;
  }

  if (status === 'STARTED') {
    state.refineById[packageId] = {
      ...base,
      stateCode: REFINE_BOQ_STATE_CODE.STARTED,
    };
    return;
  }

  if (status === 'FAILURE') {
    state.refineById[packageId] = {
      ...base,
      stateCode: REFINE_BOQ_STATE_CODE.FAILURE,
      apiErrorCode: response.error?.code || response.code || null,
    };
    return;
  }

  if (status === 'NOT_FOUND') {
    state.refineById[packageId] = {
      ...base,
      stateCode: REFINE_BOQ_STATE_CODE.NOT_FOUND,
    };
    return;
  }

  if (status !== 'SUCCESS') {
    state.refineById[packageId] = {
      ...base,
      stateCode: REFINE_BOQ_STATE_CODE.INVALID_RESPONSE,
    };
    return;
  }

  const result = normalizeRefineBoqResult(response.result);
  if (!result) {
    state.refineById[packageId] = {
      ...base,
      status: 'SUCCESS',
      stateCode: REFINE_BOQ_STATE_CODE.INVALID_RESPONSE,
    };
    return;
  }

  if (hasStructuralRefineChanges(result)) {
    state.refineById[packageId] = {
      ...base,
      status: 'SUCCESS',
      stateCode: REFINE_BOQ_STATE_CODE.UNSUPPORTED_STRUCTURAL_CHANGES,
      result,
    };
    delete state.refineReviewById[packageId];
    return;
  }

  const review = buildRefineBoqReview({
    instruction: current.instruction,
    currentRows: getBoqRowsForRefinement(state, packageId),
    result,
    units: state.units,
  });

  if (!review) {
    state.refineById[packageId] = {
      ...base,
      status: 'SUCCESS',
      stateCode: REFINE_BOQ_STATE_CODE.INVALID_RESPONSE,
      result,
    };
    delete state.refineReviewById[packageId];
    return;
  }

  state.refineById[packageId] = {
    ...base,
    status: 'SUCCESS',
    stateCode: review.rows.length
      ? REFINE_BOQ_STATE_CODE.REVIEW_READY
      : REFINE_BOQ_STATE_CODE.NO_CHANGES,
    result,
  };

  if (review.rows.length) {
    state.refineReviewById[packageId] = review;
  } else {
    delete state.refineReviewById[packageId];
  }
};

export default {
  [refineBoqWithAI.pending]: (state, action) => {
    const packageId = action.meta?.arg?.packageId;
    if (packageId == null) return;

    state.refineById = state.refineById || {};
    state.refineReviewById = state.refineReviewById || {};
    state.uiLoading = state.uiLoading || {};
    bumpRefinePollGeneration(state, packageId);
    delete state.refineReviewById[packageId];
    state.uiLoading.refinePollById = omitPackageFlag(
      state.uiLoading.refinePollById,
      packageId
    );
    state.refineById[packageId] = {
      status: 'PENDING',
      stateCode: REFINE_BOQ_STATE_CODE.PENDING,
      instruction: action.meta?.arg?.prompt ?? '',
      result: null,
      apiErrorCode: null,
      apiErrorType: null,
      apiErrorMessage: null,
      httpStatus: null,
      requestId: action.meta?.requestId,
    };
  },

  [refineBoqWithAI.fulfilled]: (state, action) => {
    if (isStalePostResponse(state, action)) return;
    applyRefineResponse(state, action);
  },

  [refineBoqWithAI.rejected]: (state, action) => {
    const packageId = action.meta?.arg?.packageId;
    if (packageId == null || isStalePostResponse(state, action)) return;
    state.refineById[packageId] = {
      ...state.refineById?.[packageId],
      status: 'ERROR',
      stateCode: REFINE_BOQ_STATE_CODE.REQUEST_FAILED,
      result: null,
      apiErrorCode: action.payload?.code || null,
      apiErrorType: action.payload?.type || null,
      apiErrorMessage: action.payload?.message || null,
      httpStatus: action.payload?.status || null,
    };
  },

  [fetchRefineBoqStatus.pending]: (state, action) => {
    const packageId = action.meta?.arg?.packageId;
    const pollGeneration = action.meta?.arg?.pollGeneration;
    if (
      packageId == null ||
      isStaleRefinePollResponse(state, packageId, pollGeneration)
    ) {
      return;
    }
    state.uiLoading = state.uiLoading || {};
    state.uiLoading.refinePollById = {
      ...state.uiLoading.refinePollById,
      [packageId]: true,
    };
  },

  [fetchRefineBoqStatus.fulfilled]: (state, action) => {
    const packageId = action.meta?.arg?.packageId;
    const pollGeneration = action.meta?.arg?.pollGeneration;
    if (
      packageId == null ||
      isStaleRefinePollResponse(state, packageId, pollGeneration)
    ) {
      return;
    }
    state.uiLoading = state.uiLoading || {};
    state.uiLoading.refinePollById = omitPackageFlag(
      state.uiLoading?.refinePollById,
      packageId
    );
    applyRefineResponse(state, action);
  },

  [fetchRefineBoqStatus.rejected]: (state, action) => {
    const packageId = action.meta?.arg?.packageId;
    const pollGeneration = action.meta?.arg?.pollGeneration;
    if (
      packageId == null ||
      isStaleRefinePollResponse(state, packageId, pollGeneration)
    ) {
      return;
    }

    state.uiLoading = state.uiLoading || {};
    state.uiLoading.refinePollById = omitPackageFlag(
      state.uiLoading?.refinePollById,
      packageId
    );

    const current = state.refineById?.[packageId] || {};
    if (
      Number(action.payload?.status) === 404 &&
      (current.status === 'PENDING' || current.status === 'STARTED')
    ) {
      return;
    }

    state.refineById[packageId] = {
      ...current,
      status: Number(action.payload?.status) === 404 ? 'NOT_FOUND' : 'ERROR',
      stateCode:
        Number(action.payload?.status) === 404
          ? REFINE_BOQ_STATE_CODE.NOT_FOUND
          : REFINE_BOQ_STATE_CODE.POLL_FAILED,
      apiErrorCode: action.payload?.code || null,
      apiErrorType: action.payload?.type || null,
      apiErrorMessage: action.payload?.message || null,
      httpStatus: action.payload?.status || null,
    };
  },
};
